import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { APIResponse } from '../models/APIResponse';
import { ToastrService } from 'ngx-toastr';
import { apiV1 } from '../common/texts-constants';

@Injectable({
  providedIn: 'root'
})
export class AuthCookieService implements OnDestroy {
  private http = inject(HttpClient);
  private msal = inject(MsalService);
  private toastr = inject(ToastrService);
  private readonly destroy$ = new Subject<void>();
  private expiryWarningTimer: ReturnType<typeof setTimeout> | null = null;


  private apiBase = environment.apiUrl+apiV1;

  async AttachCookie(scopes: string[] = ['user.read']) {
    const account = this.msal.instance.getActiveAccount() || this.msal.instance.getAllAccounts()[0];
    if (!account) throw new Error('No active account');

    let result;
    try {
      result = await this.msal.instance.acquireTokenSilent({ account, scopes });
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        result = await this.msal.instance.acquireTokenPopup({ account, scopes });
      } else {
        throw e;
      }
    }
    const token = result.idToken;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    // Call API to set HttpOnly cookie
    return firstValueFrom(this.http.post<APIResponse<void>>(`${this.apiBase}auth/attach`, null, { headers }));
  }

  LogoutCookie() {
    this.ClearExpiryWarningTimer();
    return firstValueFrom(this.http.post<APIResponse<void>>(`${this.apiBase}auth/logout`, {}));
  }

  CheckExpiryAndWarn() {
    this.http.get<{ expiry: number }>(`${this.apiBase}auth/token-expiry`, {}).pipe(takeUntil(this.destroy$)).subscribe(res => {
      const expiryMs = res.expiry * 1000;
      const warningMs = expiryMs - 15 * 60 * 1000;
      const now = Date.now();
      const msUntilWarning = warningMs - now;
      // Replace any previously scheduled warning so only one timer is ever active
      this.ClearExpiryWarningTimer();
      if (msUntilWarning > 0) {
        this.expiryWarningTimer = setTimeout(() => {
          this.expiryWarningTimer = null;
          this.toastr.info('You will be logged out in 15 minutes. Kindly save your work.', '', { timeOut: 10000 });
        }, msUntilWarning);
      }
    });
  }

  private ClearExpiryWarningTimer(): void {
    if (this.expiryWarningTimer !== null) {
      clearTimeout(this.expiryWarningTimer);
      this.expiryWarningTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.ClearExpiryWarningTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
